import React, { useEffect, useState, useCallback } from "react";
import Header from "./Header";
import { Link } from "react-router-dom";
import { api } from "../server";

const money = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number.isFinite(Number(n)) ? Number(n) : 0);

const statusColor = (s) => ({
  Ordered: "bg-secondary-subtle text-secondary",
  Accepted: "bg-info-subtle text-info",
  Picked: "bg-warning-subtle text-warning",
  Delivered: "bg-success-subtle text-success",
  Cancelled: "bg-danger-subtle text-danger",
}[s] || "bg-light text-dark");

const todayISO = () => new Date().toISOString().slice(0, 10);

function DailyReport() {
  const [date, setDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/reports/daily?date=${date}`);
      setReport(data || null);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const summary = report?.summary || {};
  const byDriver = report?.byDriver || [];
  const orders = report?.orders || [];

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Daily Report</h3>
                <div className="text-secondary lh-1">Orders • Deliveries • Revenue for a single day</div>
              </div>
              <div className="d-flex gap-1 align-items-center">
                <input type="date" className="form-control" style={{ minWidth: 160 }} value={date} onChange={(e) => setDate(e.target.value)} />
                <button className="btn btn-light border px-4" onClick={fetchReport}>
                  <i className="bi bi-arrow-clockwise me-2" />Refresh
                </button>
                <button className="btn btn-light border px-4" onClick={() => window.print()}>
                  <i className="bi bi-printer me-2" />Print
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-cart" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Total Orders</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1">{summary.totalOrders || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-check2-circle" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Delivered</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-success">{summary.delivered || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-truck" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">In Progress</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-warning">{summary.inProgress || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                      <div className="card-body p-3 d-flex align-items-center gap-2">
                        <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-x-circle" />
                        </div>
                        <div>
                          <div className="text-secondary small lh-1">Cancelled</div>
                          <div className="fs-5 fw-semibold lh-1 mt-1 text-danger">{summary.cancelled || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success" style={{ width: 40, height: 40 }}>
                        <i className="bi bi-cash-stack" />
                      </div>
                      <div>
                        <div className="text-secondary small lh-1">Revenue (delivered)</div>
                        <div className="fs-3 fw-semibold lh-1 mt-1 text-success">{money(summary.revenue)}</div>
                      </div>
                    </div>
                    <div className="text-secondary small">Date: <strong>{date}</strong></div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4 mb-2">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary" style={{ width: 40, height: 40 }}>
                        <i className="bi bi-people" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">Per-Driver Breakdown</div>
                        <div className="text-secondary small">Deliveries and revenue by driver</div>
                      </div>
                    </div>
                    {byDriver.length === 0 ? (
                      <div className="text-center py-3 text-secondary">No deliveries on this day.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Driver</th>
                              <th className="text-end">Deliveries</th>
                              <th className="text-end">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {byDriver.map((d, i) => (
                              <tr key={i}>
                                <td className="fw-semibold">{d.driverName}</td>
                                <td className="text-end fw-semibold">{d.deliveries}</td>
                                <td className="text-end fw-semibold">{money(d.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info" style={{ width: 40, height: 40 }}>
                        <i className="bi bi-list-ol" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">Orders ({orders.length})</div>
                        <div className="text-secondary small">All orders placed on this day</div>
                      </div>
                    </div>
                    {orders.length === 0 ? (
                      <div className="text-center py-3 text-secondary">No orders on this day.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Order #</th><th>Customer</th><th>Driver</th>
                              <th>Status</th><th className="text-end">Amount</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((o) => (
                              <tr key={o._id}>
                                <td className="fw-semibold">{o.orderNumber}</td>
                                <td>{o.customer?.name || "—"}</td>
                                <td>{o.driver?.name || <span className="text-warning">Unassigned</span>}</td>
                                <td><span className={`badge ${statusColor(o.status)}`}>{o.status}</span></td>
                                <td className="text-end fw-semibold">{money(o.totalAmount)}</td>
                                <td className="text-end">
                                  <Link to={`/orders/${o._id}`} className="btn btn-sm btn-light border">
                                    <i className="bi bi-eye" />
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

export default DailyReport;
